import { getBaseRole } from '@/helpers/customRoles';
import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { crmIntegrationsApi, maxBindingApi, settingApi } from '@/api'
import { useAddAlert } from '@/hooks/useAddAlert'
import { selectMessengerBindingVersion } from '@/store/messengerBindingSlice'

type UserId = string | number | null | undefined
type UserRole = string | null | undefined
type MaxBotIntegrationSettings = {
  isMaxTwoFactorAuthEnabled?: string | number | boolean
}

export type MaxBotIntegrationStatus = 'hidden' | 'activate' | 'connected'

const isEnabledSetting = (value: unknown) =>
  value === '1' || value === 1 || value === true

const isMaxHiddenForRole = (role: UserRole) =>
  getBaseRole(role) === 'children' || getBaseRole(role) === 'parent'

const parseFields = (fields: unknown) => {
  if (!fields) return {}
  if (typeof fields === 'object') return fields as Record<string, unknown>

  try {
    return JSON.parse(String(fields))
  } catch {
    return {}
  }
}

const isMaxIntegrationActive = (response: any) => {
  const data = response?.data?.data ?? response?.data ?? {}
  const fields = data?.fields
    ? parseFields(data.fields)
    : data?.field
      ? parseFields(data.field)
      : data

  return (
    fields?.is_active === true ||
    fields?.is_active === '1' ||
    fields?.is_active === 'true'
  )
}

const getErrorText = (error: any, fallback: string) => {
  const apiError = error?.response?.data?.error ?? error?.data?.error

  if (Array.isArray(apiError)) return apiError.join(', ')
  if (typeof apiError === 'string') return apiError

  return fallback
}

const isFailedResponse = (response: any) => {
  if (!response?.status || response.status < 200 || response.status >= 300) {
    return true
  }

  return response?.data?.status === 'error'
}

const getMaxTwoFactorAuthEnabled = async (
  settings: MaxBotIntegrationSettings | null | undefined,
) => {
  if (settings?.isMaxTwoFactorAuthEnabled !== undefined) {
    return isEnabledSetting(settings.isMaxTwoFactorAuthEnabled)
  }

  const response = await settingApi.getAll()

  if (isFailedResponse(response)) throw response

  const setting = response?.data?.find?.(
    (item: any) => item?.key === 'isMaxTwoFactorAuthEnabled',
  )

  return isEnabledSetting(setting?.value)
}

export const useMaxBotIntegration = (
  settings: MaxBotIntegrationSettings | null | undefined,
  userId: UserId,
  userRole?: UserRole,
) => {
  const addAlert = useAddAlert()
  const addAlertRef = useRef(addAlert)
  const messengerBindingVersion = useSelector(selectMessengerBindingVersion)
  const [maxBotIntegrationStatus, setMaxBotIntegrationStatus] =
    useState<MaxBotIntegrationStatus>('hidden')

  useEffect(() => {
    addAlertRef.current = addAlert
  }, [addAlert])

  useEffect(() => {
    if (!userId || !userRole || isMaxHiddenForRole(userRole)) {
      setMaxBotIntegrationStatus('hidden')
      return
    }

    getMaxTwoFactorAuthEnabled(settings)
      .then((isMaxTwoFactorAuthEnabled) => {
        if (!isMaxTwoFactorAuthEnabled) {
          setMaxBotIntegrationStatus('hidden')
          return null
        }

        return crmIntegrationsApi.getMaxConfig()
      })
      .then((configRes) => {
        if (!configRes) return null
        if (isFailedResponse(configRes)) throw configRes
        if (!isMaxIntegrationActive(configRes)) {
          setMaxBotIntegrationStatus('hidden')
          return null
        }

        return maxBindingApi.checkIsActive({ user_id: userId })
      })
      .then((bindingRes) => {
        if (!bindingRes) return
        if (isFailedResponse(bindingRes)) throw bindingRes

        setMaxBotIntegrationStatus(
          bindingRes?.data?.data?.is_active === true
            ? 'connected'
            : 'activate',
        )
      })
      .catch((error) => {
        setMaxBotIntegrationStatus('hidden')
        addAlertRef.current({
          title: 'Ошибка',
          text: getErrorText(error, 'Не удалось проверить привязку MAX'),
          type: 'error',
        })
      })
  }, [settings?.isMaxTwoFactorAuthEnabled, userId, userRole, messengerBindingVersion])

  return maxBotIntegrationStatus
}
